import { AlertCircle } from "lucide-react";
import { SlashCommandsPreview } from "@/components/agents/SlashCommandsPreview";
import { ModelSelector } from "@/components/chat/ModelSelector";
import { TeamSelector } from "@/components/shared";
import { featureFlags } from "@/config/featureFlags";
import { useApp } from "@/context/AppContext";
import { formatDateOnly } from "@/lib/formatters";
import type { DataCollection, ModelTierDefault } from "@/types";
import type { AIModel } from "@/types/models";

// ============================================================================
// Agent Form Fields — shared between Create and Edit modals
// ============================================================================

export interface AgentFormData {
  name: string;
  description: string;
  color: string;
  systemPrompt: string;
  modelId: string;
  teamUuid: string;
  dataCollectionIds: string[];
  routingKeywords: string;
}

export interface AgentFormFieldsProps {
  /** Unique prefix for input IDs to avoid collisions when multiple forms exist. */
  idPrefix: string;
  formData: AgentFormData;
  onChange: (data: AgentFormData) => void;
  models: AIModel[];
  tierDefaults?: ModelTierDefault[];
  dataSources: DataCollection[];
  /**
   * Whether to show the team-association selector. Defaults to false — the
   * agent's team is inferred from the creation context (a team/project page, or
   * the "Everyone" team from the general Agents screen) and does NOT grant
   * access, so surfacing it in the form is confusing.
   */
  showTeam?: boolean;
}

const AGENT_COLORS = ["#EC622B", "#7C16F5", "#10B981", "#F59E0B", "#EF4444", "#06B6D4"];

export function AgentFormFields({
  idPrefix,
  formData,
  onChange,
  models,
  tierDefaults = [],
  dataSources,
  showTeam = false,
}: Readonly<AgentFormFieldsProps>) {
  const { teams, models: allModels } = useApp();
  // All listed models (incl. disabled deprecated ones); "tier:<x>" values never match.
  const selectedModel = allModels.find((m) => m.id === formData.modelId);
  const replacement = models.find((m) => m.id === selectedModel?.replacementModelId && !m.isDeprecated);
  return (
    <>
      <div>
        <label htmlFor={`${idPrefix}-name`} className="block text-sm font-medium text-content-secondary mb-2">
          Agent Name
        </label>
        <input
          id={`${idPrefix}-name`}
          type="text"
          value={formData.name}
          onChange={(e) => onChange({ ...formData, name: e.target.value })}
          placeholder="e.g., Code Assistant"
          className="w-full px-4 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary"
          required
        />
      </div>

      <div>
        <label htmlFor={`${idPrefix}-description`} className="block text-sm font-medium text-content-secondary mb-2">
          Description
        </label>
        <textarea
          id={`${idPrefix}-description`}
          value={formData.description}
          onChange={(e) => onChange({ ...formData, description: e.target.value })}
          placeholder="Brief description of what this agent does"
          className="w-full px-4 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary h-24 resize-none"
        />
        {featureFlags.agentRouting && (
          <p className="mt-1.5 text-xs text-content-tertiary">
            Auto mode picks agents by this description, so say which questions this agent answers.
          </p>
        )}
      </div>

      {featureFlags.agentRouting && (
        <div>
          <label
            htmlFor={`${idPrefix}-routing-keywords`}
            className="block text-sm font-medium text-content-secondary mb-2"
          >
            Routing keywords
          </label>
          <input
            id={`${idPrefix}-routing-keywords`}
            type="text"
            value={formData.routingKeywords}
            onChange={(e) => onChange({ ...formData, routingKeywords: e.target.value })}
            placeholder="e.g. PTO, leave policy, benefits"
            className="w-full px-4 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary"
          />
          <p className="mt-1.5 text-xs text-content-tertiary">
            Optional, comma-separated. Topics that should bring a new Auto chat to this agent.
          </p>
        </div>
      )}

      <div role="group" aria-label="Color">
        <span className="block text-sm font-medium text-content-secondary mb-2">Color</span>
        <div className="flex gap-2">
          {AGENT_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => onChange({ ...formData, color: c })}
              className={`w-10 h-10 rounded-lg transition ${
                formData.color === c ? "ring-2 ring-white ring-offset-2 ring-offset-surface-primary" : ""
              }`}
              style={{ backgroundColor: c }}
            />
          ))}
        </div>
      </div>

      <div>
        <label htmlFor={`${idPrefix}-system-prompt`} className="block text-sm font-medium text-content-secondary mb-2">
          System Prompt
        </label>
        <textarea
          id={`${idPrefix}-system-prompt`}
          value={formData.systemPrompt}
          onChange={(e) => onChange({ ...formData, systemPrompt: e.target.value })}
          placeholder="You are a helpful assistant that..."
          className="w-full px-4 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary h-32 max-h-96 resize-y"
          required
        />
        <SlashCommandsPreview systemPrompt={formData.systemPrompt} />
      </div>

      <div>
        <ModelSelector
          aria-label="AI Model"
          value={formData.modelId}
          onChange={(v) => onChange({ ...formData, modelId: v })}
          models={models}
          tierDefaults={tierDefaults}
        />
        {selectedModel?.isDeprecated && (
          <output className="mt-2 flex items-start gap-2 px-3 py-2 bg-amber-500/10 border border-amber-500/20 rounded-lg text-xs text-amber-800 dark:text-amber-200">
            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>
              {selectedModel.name} is deprecated
              {selectedModel.sunsetDate && ` (sunset ${formatDateOnly(selectedModel.sunsetDate)})`}.
              {replacement && ` Consider switching to ${replacement.name}.`}
            </span>
          </output>
        )}
      </div>

      {showTeam && (
        <TeamSelector
          teamId={teams.find((t) => t.uuid === formData.teamUuid)?.id}
          onTeamChange={(_teamId, teamUuid) => onChange({ ...formData, teamUuid })}
        />
      )}

      {dataSources.length > 0 && (
        <div>
          <span className="block text-sm font-medium text-content-secondary mb-2">Data Sources</span>
          <div className="space-y-2">
            {dataSources.map((source) => (
              <label
                key={source.uuid}
                htmlFor={`${idPrefix}-datasource-${source.uuid}`}
                aria-label={source.name}
                className="flex items-center gap-3 p-3 bg-surface-secondary rounded-lg cursor-pointer hover:bg-interactive-active transition"
              >
                <input
                  id={`${idPrefix}-datasource-${source.uuid}`}
                  type="checkbox"
                  checked={formData.dataCollectionIds.includes(source.uuid)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onChange({
                        ...formData,
                        dataCollectionIds: [...formData.dataCollectionIds, source.uuid],
                      });
                    } else {
                      onChange({
                        ...formData,
                        dataCollectionIds: formData.dataCollectionIds.filter((id) => id !== source.uuid),
                      });
                    }
                  }}
                  className="w-4 h-4 text-brand-primary bg-surface-tertiary border-border-hover rounded-sm focus:ring-brand-primary"
                />
                <div className="flex-1">
                  <div className="text-content-primary text-sm font-medium">{source.name}</div>
                  <div className="text-content-tertiary text-xs capitalize">{source.sourceType.replace("_", " ")}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
